import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker, Field, inputCls } from "@/components/form";
import { api, formatApiError } from "@/lib/api";
import { CONTACT, AREA_OPTIONS } from "@/data/site";

export default function Kontakt() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_area: "versicherung", message: "", gdpr_consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { ...form, source: "kontaktformular" });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", business_area: "versicherung", message: "", gdpr_consent: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="kontakt-page" className="bg-navy-900">
      <Seo title="Kontakt" description="Nehmen Sie Kontakt mit VM-FFM.DE auf. Wir beraten Sie unabhängig zu Versicherung, Finanzierung und Immobilien." path="/kontakt" />
      <section className="pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Kicker>Kontakt</Kicker>
            <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-none">Wir sind für Sie da.</h1>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-md">Schreiben Sie uns oder rufen Sie an. Wir melden uns innerhalb eines Werktages bei Ihnen.</p>
            <div className="mt-10 space-y-6">
              {[[MapPin, CONTACT.city], [Phone, CONTACT.phone], [Mail, CONTACT.email], [Clock, CONTACT.hours]].map(([Icon, val], i) => (
                <div key={i} className="flex items-center gap-4"><div className="w-11 h-11 rounded-sm bg-navy-800 border border-white/5 flex items-center justify-center"><Icon size={18} className="text-gold-500" /></div><span className="text-slate-200">{val}</span></div>
              ))}
            </div>
          </motion.div>

          <motion.form data-testid="lead-form" onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-navy-800 border border-white/5 rounded-md p-8 lg:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name *"><input data-testid="lead-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Ihr Name" /></Field>
              <Field label="Telefon"><input data-testid="lead-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Optional" /></Field>
            </div>
            <Field label="E-Mail *"><input data-testid="lead-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="ihre@email.de" /></Field>
            <Field label="Ihr Anliegen">
              <select data-testid="lead-area" className={inputCls} value={form.business_area} onChange={set("business_area")}>
                {AREA_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-navy-800">{o.label}</option>)}
              </select>
            </Field>
            <Field label="Nachricht"><textarea data-testid="lead-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Wie können wir helfen?" /></Field>
            <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
              <input data-testid="lead-gdpr" type="checkbox" checked={form.gdpr_consent} onChange={set("gdpr_consent")} className="mt-0.5 accent-gold-500 w-4 h-4" />
              <span>Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung meiner Daten zur Kontaktaufnahme zu.</span>
            </label>
            <button data-testid="lead-submit" disabled={loading} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-4 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors disabled:opacity-60">
              {loading ? "Wird gesendet…" : <>Nachricht senden <Send size={16} /></>}
            </button>
          </motion.form>
        </div>
      </section>
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-md overflow-hidden border border-white/10" data-testid="google-maps">
            <iframe title="Standort VM-FFM.DE" width="100%" height="360" style={{ border: 0, filter: "grayscale(0.3) contrast(1.1)" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Frankfurt%20am%20Main%20Innenstadt&output=embed"></iframe>
          </div>
        </div>
      </section>
    </div>
  );
}
